import Skeleton from '@mui/material/Skeleton';
import Stack, { type StackProps } from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { lazy } from 'react';
import { type UseFormSetValue } from 'react-hook-form';
import { type Facets } from '../../../_types/Car';
import CarListSort from '../../../components/car-list-sort';
import ChipFilters from '../../../components/chips-filter';
import { useRootContainerContext } from '../../../context/root-container';
import { useSettingsContext } from '../../../context/settings';
import { useResponsive } from '../../../hooks/use-responsive';
import useLocales from '../../../hooks/useLocales';
import getStyleQuery from '../../../utils/tools/getStyleQuery';
import { type Modules } from '../../_definition/types';
import { type FahrzeugsucheFilterForm } from '../types';
import type { FilterMap } from './maps/filterMap';

const LocationMap = lazy(() => import('../../../components/location-map/map' /* webpackChunkName: "LocationMap" */));

type FahrzeugSucheFilterChipsProps = StackProps & {
    canReset: boolean;
    facets: Facets;
    filterElements: FilterMap;
    filters: FahrzeugsucheFilterForm;
    loading: boolean;
    module: Modules;
    onFilters: UseFormSetValue<FahrzeugsucheFilterForm>;
    onResetFilters: VoidFunction;
    results: number;
};

export default function FahrzeugSucheFilterChips({ canReset, facets, filterElements, filters, loading, module, onFilters, onResetFilters, results, ...other }: FahrzeugSucheFilterChipsProps) {
    const { translate } = useLocales();
    const settings = useSettingsContext();
    const rootContainer = useRootContainerContext();
    const md = useResponsive(rootContainer, 'down', 'lg');
    const sm = useResponsive(rootContainer, 'down', 'md');

    const locationMap = <LocationMap facets={facets} filters={filters} module={module} onFilters={onFilters} />;

    const numFound = (
        <Typography component="p" variant="subtitle1" sx={{ flexShrink: 0, fontWeight: 'bold !important', textAlign: { xs: 'center', sm: 'left' } }}>
            {loading ? (
                <Skeleton variant="text" width={canReset && !sm ? 225 : 150} />
            ) : (
                `${results || 0} ${(translate(`common:carSearch.foundCar${results === 1 ? '' : 's'}`) as string)?.replace('{0}', '')} ${canReset && !sm ? `${translate('common:carSearch.forFilters')}:` : ''}`
            )}
        </Typography>
    );

    return (
        !md && (
            <Stack
                data-testid="FahrzeugSucheFilterChips_results"
                alignItems="flex-start"
                justifyContent={{ xs: 'center', md: 'flex-start' }}
                direction="row"
                gap={{ xs: 0, md: 1.5 }}
                flexGrow={1}
                width={1}
                {...other}
            >
                {sm ? (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ [getStyleQuery()]: { width: 1 } }}>
                        {numFound}
                        <Stack direction="row">
                            {settings?.fahrzeugsuche?.useLocationMap && locationMap}
                            <CarListSort filters={filters} module={module} onFilters={onFilters} />
                        </Stack>
                    </Stack>
                ) : (
                    numFound
                )}

                {!sm && <ChipFilters canReset={canReset} facets={facets} filterElements={filterElements} filters={filters} onFilters={onFilters} onResetFilters={onResetFilters} />}

                {settings?.fahrzeugsuche?.useLocationMap && !sm && locationMap}
            </Stack>
        )
    );
}
